import React from 'react';
import { Card, Row, Col, Statistic, Table, Typography } from 'antd';
import { SalesTier } from '../../services/DashboardService';
import { formatCurrency, formatPercent } from '../../utils/formatters';

const { Text } = Typography;

interface DashboardSummaryProps {
  salesTiers: SalesTier[];
  viewMode: 'weekly' | 'quarterly';
  isMobile: boolean;
}

export const DashboardSummary: React.FC<DashboardSummaryProps> = ({
  salesTiers,
  viewMode,
  isMobile,
}) => {
  // Calculate overall summary metrics
  const totalLocations = salesTiers.reduce((sum, tier) => sum + tier.locations.length, 0);

  // For weekly view, use eightWeekTotalSales (Prior Week Total Sales) to match Expense tab
  // For quarterly view, use eightWeekTotalSales (which holds quarterly total sales)
  const totalSales = salesTiers.reduce((sum, tier) => {
    return sum + tier.locations.reduce((locSum, loc) => {
      // eightWeekTotalSales contains Prior Week Sales in weekly view, Quarterly Total in quarterly view
      return locSum + (loc.eightWeekTotalSales || 0);
    }, 0);
  }, 0);

  const totalGfs = salesTiers.reduce((sum, tier) => {
    return sum + tier.locations.reduce((locSum, loc) => {
      // eightWeekTotalGfs contains Prior Week GFS in weekly view, Quarterly Total in quarterly view
      return locSum + (loc.eightWeekTotalGfs || 0);
    }, 0);
  }, 0);

  const overallGfsPercent = totalSales > 0 ? (totalGfs / totalSales) * 100 : 0;

  // Calculate tier breakdown data
  const tierBreakdown = salesTiers.map(tier => {
    const tierSales = tier.locations.reduce((sum, loc) => {
      // Use eightWeekTotalSales for both views (Prior Week in weekly, Quarterly Total in quarterly)
      return sum + (loc.eightWeekTotalSales || 0);
    }, 0);
    const tierGfs = tier.locations.reduce((sum, loc) => {
      // Use eightWeekTotalGfs for both views
      return sum + (loc.eightWeekTotalGfs || 0);
    }, 0);
    const tierGfsPercent = tierSales > 0 ? (tierGfs / tierSales) * 100 : 0;

    return {
      key: tier.tierName,
      tier: tier.tierName,
      description: tier.tierRange,
      locations: tier.locations.length,
      totalSales: tierSales,
      totalGfs: tierGfs,
      avgGfsPercent: tier.avgGfsPercent,
      calculatedGfsPercent: tierGfsPercent,
    };
  }).filter(tier => tier.locations > 0);

  return (
    <Card size="small" style={{ marginBottom: 16 }}>
      {/* Top Summary Row */}
      <Row gutter={[24, 16]} style={{ marginBottom: 20 }}>
        <Col xs={12} sm={8} md={4}>
          <Statistic
            title={viewMode === 'weekly' ? 'Prior Wk Sales' : 'Qtr Total Sales'}
            value={totalSales}
            precision={0}
            prefix="$"
            valueStyle={{ fontSize: isMobile ? 16 : 18 }}
            formatter={(value) => Number(value).toLocaleString()}
          />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Statistic
            title={viewMode === 'weekly' ? 'Prior Wk GFS' : 'Qtr Total GFS'}
            value={totalGfs}
            precision={0}
            prefix="$"
            valueStyle={{ fontSize: isMobile ? 16 : 18 }}
            formatter={(value) => Number(value).toLocaleString()}
          />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Statistic
            title="Overall GFS %"
            value={overallGfsPercent}
            precision={1}
            suffix="%"
            valueStyle={{ fontSize: isMobile ? 16 : 18 }}
          />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Statistic
            title="Locations"
            value={totalLocations}
            valueStyle={{ fontSize: isMobile ? 16 : 18 }}
          />
        </Col>
      </Row>

      {/* Tier Breakdown */}
      <Text strong style={{ display: 'block', marginBottom: 12 }}>By Tier Breakdown</Text>
      {isMobile ? (
        // Mobile: Card-based tier breakdown
        <div>
          {tierBreakdown.map((tier) => (
            <div
              key={tier.key}
              style={{
                padding: '12px',
                marginBottom: 8,
                backgroundColor: '#fafafa',
                borderRadius: 6,
                border: '1px solid #f0f0f0',
              }}
            >
              <div style={{ marginBottom: 8 }}>
                <Text strong style={{ fontSize: 14 }}>{tier.tier}</Text>
                <Text type="secondary" style={{ marginLeft: 6, fontSize: 11 }}>
                  ({tier.description})
                </Text>
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '8px 16px',
              }}>
                <div>
                  <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>Locations</Text>
                  <Text strong style={{ fontSize: 14 }}>{tier.locations}</Text>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>Avg GFS %</Text>
                  <Text strong style={{ fontSize: 14 }}>{formatPercent(tier.avgGfsPercent)}</Text>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>
                    {viewMode === 'weekly' ? 'Prior Wk Sales' : 'Qtr Sales'}
                  </Text>
                  <Text style={{ fontSize: 13 }}>{formatCurrency(tier.totalSales)}</Text>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>
                    {viewMode === 'weekly' ? 'Prior Wk GFS' : 'Qtr GFS'}
                  </Text>
                  <Text style={{ fontSize: 13 }}>{formatCurrency(tier.totalGfs)}</Text>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Desktop: Table view
        <Table
          columns={[
            {
              title: 'Tier',
              key: 'tier',
              width: 200,
              render: (_: any, record: any) => (
                <span>
                  <Text strong>{record.tier}</Text>
                  <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>({record.description})</Text>
                </span>
              ),
            },
            {
              title: 'Locations',
              dataIndex: 'locations',
              key: 'locations',
              width: 100,
              align: 'right' as const,
            },
            {
              title: viewMode === 'weekly' ? 'Prior Wk Sales' : 'Qtr Sales',
              dataIndex: 'totalSales',
              key: 'totalSales',
              width: 140,
              align: 'right' as const,
              render: (val: number) => formatCurrency(val),
            },
            {
              title: viewMode === 'weekly' ? 'Prior Wk GFS' : 'Qtr GFS',
              dataIndex: 'totalGfs',
              key: 'totalGfs',
              width: 140,
              align: 'right' as const,
              render: (val: number) => formatCurrency(val),
            },
            {
              title: 'Avg GFS %',
              dataIndex: 'avgGfsPercent',
              key: 'avgGfsPercent',
              width: 100,
              align: 'right' as const,
              render: (val: number) => formatPercent(val),
            },
          ]}
          dataSource={tierBreakdown}
          pagination={false}
          size="small"
          bordered
        />
      )}
    </Card>
  );
};
